import { useState } from 'react'
import type React from 'react'

interface DragPoint {
    list: string
    index: number
}

/**
 * Reordenar renglones arrastrando la manija (⋮⋮). Solo la manija es `draggable` para no
 * interferir con la selección de texto de los inputs; el renglón completo es zona de soltar.
 */
export const useRowDrag = () => {
    const [drag, setDrag] = useState<DragPoint | null>(null)
    const [over, setOver] = useState<DragPoint | null>(null)

    const reset = () => {
        setDrag(null)
        setOver(null)
    }

    const handleProps = (list: string, index: number, enabled = true) => ({
        draggable: enabled,
        'aria-disabled': !enabled,
        onDragStart: (event: React.DragEvent<HTMLElement>) => {
            if (!enabled) return
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', String(index))
            const row = event.currentTarget.closest('[data-drag-row]')
            if (row instanceof HTMLElement) event.dataTransfer.setDragImage(row, 12, row.offsetHeight / 2)
            setDrag({ list, index })
        },
        onDragEnd: reset,
    })

    const rowProps = (list: string, index: number, onMove: (from: number, to: number) => void, canDrop = true) => ({
        'data-drag-row': true,
        onDragOver: (event: React.DragEvent<HTMLElement>) => {
            if (!drag || drag.list !== list || !canDrop) return
            event.preventDefault()
            if (over?.list !== list || over.index !== index) setOver({ list, index })
        },
        onDrop: (event: React.DragEvent<HTMLElement>) => {
            event.preventDefault()
            if (drag && drag.list === list && canDrop && drag.index !== index) onMove(drag.index, index)
            reset()
        },
    })

    const isOver = (list: string, index: number) =>
        Boolean(drag && over && drag.list === list && over.list === list && over.index === index && drag.index !== index)

    return { handleProps, rowProps, isOver }
}
